import * as IF from "infrontjs";
import L from "leaflet";
import template from "./detail.html";
import { _lcs, locale } from "../../i18n";

// Every *_TITLES map below holds the German source string, which is also its
// dictionary key: module constants are evaluated before I18n exists, so the
// translation happens at the point of use.
const ACTIVITY_TITLES = {
    hiking : "Wandern", walking : "Spazieren", cycling : "Radfahren",
    road_cycling : "Rennrad", mountain_biking : "Mountainbike",
    pilgrimage : "Pilgerweg", paddling : "Kanu", horse_riding : "Reiten",
    running : "Laufen", climbing : "Klettern", winter_hiking : "Winterwandern",
    snowshoe : "Schneeschuh", cross_country_skiing : "Langlauf",
    sightseeing : "Stadtrundgang", other : "Sonstiges"
};

const DIFFICULTY_TITLES = {
    unknown : "Ohne Angabe", easy : "Leicht", moderate : "Mittel",
    difficult : "Schwer", expert : "Sehr schwer"
};

const ROUTE_TYPE_TITLES = {
    loop : "Rundweg", point_to_point : "Streckentour", out_and_back : "Hin und zurück"
};

const SURFACE_TITLES = {
    paved : "Asphalt", gravel : "Schotter", trail : "Pfad",
    rock : "Fels", grass : "Wiese"
};

const SEASON_TITLES = {
    spring : "Frühling", summer : "Sommer", autumn : "Herbst", winter : "Winter"
};

/**
 * <tour-detail> — the full record behind a row of the Touren table.
 *
 * Read-only apart from publishing, like <place-detail>, and for the same
 * reason: everything a tour knows comes from an importer.
 *
 * The one thing it adds is a drawing of the route. A tour that cannot be seen
 * is a list of numbers, and an operator deciding whether to publish one wants
 * to know it is not a straight line between two villages because the provider
 * shipped a broken track.
 *
 * The sketch is an inline SVG polyline built from the stored coordinates: no
 * map library, no tile requests, no API key — so it renders instantly, offline
 * and in print. It shows the SHAPE of a route, not where it is; a basemap
 * underneath is a separate, additive job.
 */
class TourDetail extends HTMLElement
{
    /**
     * Coordinates drawn at most. A 2400-point track renders identically at
     * 400 and costs a fifth of the DOM.
     */
    static MAX_TRACK_POINTS = 400;

    /**
     * How far off the path still counts as "along the route". 500 m is roughly
     * a five-minute detour on foot — near enough to be worth the walk, far
     * enough to catch the hut just off the ridge.
     */
    static NEARBY_RADIUS_M = 500;

    constructor()
    {
        super();
        this._connected = false;
        this._tour = null;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );

        this._modal = this.querySelector( '[data-id="tr-modal"]' );
        this._title = this.querySelector( '[data-id="tr-title"]' );
        this._error = this.querySelector( '[data-id="tr-error"]' );
        this._publish = this.querySelector( '[data-id="tr-publish"]' );
        this._publishLabel = this.querySelector( '[data-id="tr-publish-label"]' );
        this._hint = this.querySelector( '[data-id="tr-hint"]' );

        // Built on first use and torn down with the dialog — Leaflet needs a
        // container that is actually laid out, and a hidden modal is not.
        this._map = null;
        this._mapLayer = null;
    }

    bind()
    {
        this.querySelector( '[data-id="tr-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="tr-cancel"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="tr-backdrop"]' ).addEventListener( "click", () => this.close() );
        this._publish.addEventListener( "click", () => this.togglePublished() );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
        // The state can exit with the dialog still open; Leaflet keeps window
        // listeners until it is told to let go.
        this._destroyMap();
    }

    /* ------------------------------- Public API ------------------------------ */

    open( tour, sourceName )
    {
        this._tour = tour;
        this._clearError();

        this._title.textContent = tour.name || _lcs( "Tour" );
        this._set( "tr-key", [ tour.slug, tour.sourceId ].filter( Boolean ).join( " · " ) || "–" );

        this._renderBadges( tour );
        this._renderStats( tour );
        this._renderTrack( tour );
        this._renderImages( tour );
        this._renderTexts( tour );
        this._renderRoute( tour );
        this._renderConditions( tour );
        this._renderProvenance( tour, sourceName );
        this._renderPublishAction( tour );

        this._modal.hidden = false;

        // Deliberately last, AFTER the dialog is visible. Leaflet measures its
        // container when the map is created, and a container inside a `hidden`
        // modal measures 0×0 — the map then lays every tile out against the
        // wrong size and never recovers on its own.
        this._renderMap( tour );
    }

    close()
    {
        this._modal.hidden = true;
        this._clearError();
        this._destroyMap();
    }

    /* -------------------------------- Sections ------------------------------- */

    _renderBadges( tour )
    {
        const badges = [
            '<span class="plc-detail__badge plc-detail__badge--' + this._escape( tour.status ) + '">' +
                this._escape( this._statusLabel( tour.status ) ) + '</span>',
            '<span class="plc-detail__badge">' +
                this._escape( ACTIVITY_TITLES[ tour.activityType ]
                    ? _lcs( ACTIVITY_TITLES[ tour.activityType ] ) : tour.activityType ) + '</span>',
            '<span class="plc-detail__badge">' +
                this._escape( ROUTE_TYPE_TITLES[ tour.routeType ]
                    ? _lcs( ROUTE_TYPE_TITLES[ tour.routeType ] ) : tour.routeType ) + '</span>'
        ];

        // Only worth a badge when somebody actually graded it.
        if ( tour.difficulty && tour.difficulty !== "unknown" )
        {
            badges.push( '<span class="plc-detail__badge">' +
                this._escape( DIFFICULTY_TITLES[ tour.difficulty ]
                    ? _lcs( DIFFICULTY_TITLES[ tour.difficulty ] ) : tour.difficulty ) + '</span>' );
        }
        if ( tour.isLocked )
        {
            badges.push( '<span class="plc-detail__badge plc-detail__badge--lock">' +
                '<i class="icon icon--lock"></i> ' + this._escape( _lcs( "Manuell bearbeitet" ) ) + '</span>' );
        }
        if ( tour.isDuplicate )
        {
            badges.push( '<span class="plc-detail__badge plc-detail__badge--dupe">' +
                '<i class="icon icon--layers"></i> ' + this._escape( _lcs( "Dublette" ) ) + '</span>' );
        }

        this.querySelector( '[data-id="tr-badges"]' ).innerHTML = badges.join( "" );
    }

    /** The four numbers a route is chosen by. */
    _renderStats( tour )
    {
        const stats = [
            [ _lcs( "Distanz" ), tour.distanceKm !== null && tour.distanceKm !== undefined ? tour.distanceKm + " km" : null ],
            [ _lcs( "Dauer" ), this._durationText( tour.durationMinutes ) ],
            [ _lcs( "Aufstieg" ), tour.ascentMeters !== null && tour.ascentMeters !== undefined ? tour.ascentMeters + " hm" : null ],
            [ _lcs( "Abstieg" ), tour.descentMeters !== null && tour.descentMeters !== undefined ? tour.descentMeters + " hm" : null ],
            [ _lcs( "Höhe" ), tour.minAltitudeMeters !== null && tour.maxAltitudeMeters !== null &&
                      tour.minAltitudeMeters !== undefined && tour.maxAltitudeMeters !== undefined
                ? tour.minAltitudeMeters + "–" + tour.maxAltitudeMeters + " m" : null ]
        ].filter( ( [ , value ] ) => value !== null && value !== undefined );

        this.querySelector( '[data-id="tr-stats"]' ).innerHTML = stats.map( ( [ label, value ] ) =>
            '<div class="tr-detail__stat">' +
                '<dt>' + this._escape( label ) + '</dt>' +
                '<dd>' + this._escape( value ) + '</dd>' +
            '</div>'
        ).join( "" );
    }

    /**
     * The route as an inline SVG polyline.
     *
     * Coordinates are projected flat, with longitude squeezed by the cosine of
     * the latitude so the shape is not stretched sideways — over a tour's few
     * kilometres that is indistinguishable from a proper projection and needs
     * no library.
     */
    _renderTrack( tour )
    {
        const figure = this.querySelector( '[data-id="tr-track"]' );
        const svg = this.querySelector( '[data-id="tr-track-svg"]' );
        const caption = this.querySelector( '[data-id="tr-track-caption"]' );

        this._hasSketch = false;

        const coordinates = tour.geometry && Array.isArray( tour.geometry.coordinates )
            ? tour.geometry.coordinates
            : [];

        if ( coordinates.length < 2 )
        {
            figure.hidden = true;
            svg.innerHTML = "";
            return;
        }

        const step = Math.max( Math.ceil( coordinates.length / TourDetail.MAX_TRACK_POINTS ), 1 );
        const sampled = coordinates.filter( ( _, index ) => index % step === 0 );
        // Always keep the finish, whatever the sampling does.
        if ( sampled[ sampled.length - 1 ] !== coordinates[ coordinates.length - 1 ] )
        {
            sampled.push( coordinates[ coordinates.length - 1 ] );
        }

        const lats = sampled.map( ( c ) => c[ 1 ] );
        const lons = sampled.map( ( c ) => c[ 0 ] );
        const minLat = Math.min( ...lats ), maxLat = Math.max( ...lats );
        const minLon = Math.min( ...lons ), maxLon = Math.max( ...lons );

        const cos = Math.cos( ( ( minLat + maxLat ) / 2 ) * Math.PI / 180 );
        const width = Math.max( ( maxLon - minLon ) * cos, 1e-6 );
        const height = Math.max( maxLat - minLat, 1e-6 );
        const scale = 96 / Math.max( width, height );

        const offsetX = ( 100 - width * scale ) / 2;
        const offsetY = ( 100 - height * scale ) / 2;

        const points = sampled.map( ( [ lon, lat ] ) =>
        {
            const x = offsetX + ( lon - minLon ) * cos * scale;
            // SVG y grows downwards; north belongs at the top.
            const y = offsetY + ( maxLat - lat ) * scale;
            return `${x.toFixed( 2 )},${y.toFixed( 2 )}`;
        } ).join( " " );

        const first = points.split( " " )[ 0 ].split( "," );
        const last = points.split( " " ).pop().split( "," );

        svg.innerHTML =
            '<polyline points="' + points + '" fill="none" stroke="currentColor" stroke-width="1.4" ' +
                'stroke-linejoin="round" stroke-linecap="round" />' +
            '<circle cx="' + first[ 0 ] + '" cy="' + first[ 1 ] + '" r="2.2" class="tr-detail__start" />' +
            ( tour.routeType === "loop" ? "" :
                '<circle cx="' + last[ 0 ] + '" cy="' + last[ 1 ] + '" r="2.2" class="tr-detail__end" />' );

        caption.textContent =
            _lcs( "{count} Streckenpunkte", { count: coordinates.length.toLocaleString( locale() ) } ) +
            ( step > 1 ? " " + _lcs( "(jeder {step}. gezeichnet)", { step } ) : "" );

        // Drawn but not shown: _renderMap() reveals this only if the basemap
        // cannot render. Two pictures of the same route stacked on top of each
        // other is noise — the sketch earns its place as the FALLBACK, not as
        // a second view.
        this._hasSketch = true;
        figure.hidden = true;
    }

    /** Show the SVG sketch — the map could not be drawn. */
    _showSketchFallback()
    {
        const figure = this.querySelector( '[data-id="tr-track"]' );
        figure.hidden = !this._hasSketch;
    }

    /**
     * The route on a real basemap, under the sketch.
     *
     * Additive by design: the SVG above needs no network and always renders,
     * so a missing key, a rate-limited provider or an offline laptop costs the
     * shape of the route, not the ability to look at the record.
     *
     * Built here rather than in render() because Leaflet measures its
     * container, and a container inside a `hidden` modal has no size — the map
     * would come up 0×0 and stay that way.
     */
    _renderMap( tour )
    {
        const figure = this.querySelector( '[data-id="tr-map"]' );
        const hint = this.querySelector( '[data-id="tr-map-hint"]' );

        this._destroyMap();
        figure.hidden = true;
        hint.hidden = true;

        const config = this.app.config.get( 'map' ) || {};
        const coordinates = tour.geometry && Array.isArray( tour.geometry.coordinates )
            ? tour.geometry.coordinates
            : [];

        if ( coordinates.length < 2 )
        {
            return;
        }

        if ( !config.tileUrl )
        {
            // Say why there is no map rather than leaving a gap somebody has
            // to go and read the source to explain.
            hint.textContent = _lcs( "Keine Kartenquelle konfiguriert — der Streckenverlauf wird als Skizze angezeigt. Kachel-URL in config.json unter „map.tileUrl“ hinterlegen." );
            hint.hidden = false;
            this._showSketchFallback();
            return;
        }

        // Leaflet takes [ latitude, longitude ]; the track is GeoJSON order.
        const latLngs = coordinates.map( ( [ lon, lat ] ) => [ lat, lon ] );

        try
        {
            this._map = L.map( this.querySelector( '[data-id="tr-map-canvas"]' ), {
                "scrollWheelZoom" : false,   // the dialog scrolls; the map must not steal it
                "attributionControl" : true
            } );

            this._mapLayer = L.tileLayer( config.tileUrl, {
                // Attribution is a licence condition of every OSM-derived
                // basemap, exactly like the image credits.
                "attribution" : config.attribution || "",
                "maxZoom" : +config.maxZoom || 17
            } ).addTo( this._map );

            const line = L.polyline( latLngs, {
                "color" : "#2f6f4e",
                "weight" : 4,
                "opacity" : 0.9,
                "lineJoin" : "round",
                "lineCap" : "round"
            } ).addTo( this._map );

            this._addEndpointMarker( latLngs[ 0 ], "start", tour.startPoint && tour.startPoint.name );
            if ( tour.routeType !== "loop" )
            {
                const end = latLngs[ latLngs.length - 1 ];
                this._addEndpointMarker( end, "end", tour.endPoint && tour.endPoint.name );
            }

            this._renderWaypoints( tour );

            L.control.scale( { "imperial" : false } ).addTo( this._map );

            this._map.fitBounds( line.getBounds(), { "padding" : [ 24, 24 ] } );

            figure.hidden = false;
            this._setMapCaption();

            // The container only gets its real size once the modal is shown.
            requestAnimationFrame( () =>
            {
                if ( this._map )
                {
                    this._map.invalidateSize();
                    this._map.fitBounds( line.getBounds(), { "padding" : [ 24, 24 ] } );
                }
            } );

            // Fetched after the map is up, so a slow or failing lookup costs
            // the pins along the route, never the route itself.
            this._loadNearbyPlaces( tour );
        }
        catch ( e )
        {
            console.error( e );
            this._destroyMap();
            figure.hidden = true;
            hint.textContent = _lcs( "Die Karte konnte nicht geladen werden. Der Streckenverlauf wird als Skizze angezeigt." );
            hint.hidden = false;
            this._showSketchFallback();
        }
    }

    /**
     * Start and finish pins.
     *
     * `divIcon` rather than Leaflet's default marker: the default pulls PNGs
     * by a runtime-resolved path that bundlers break, and a CSS circle matches
     * the design system anyway.
     */
    _addEndpointMarker( latLng, kind, label )
    {
        const marker = L.marker( latLng, {
            "icon" : L.divIcon( {
                "className" : "",
                "html" : '<span class="tr-map__pin tr-map__pin--' + kind + '"></span>',
                "iconSize" : [ 16, 16 ],
                "iconAnchor" : [ 8, 8 ]
            } ),
            "keyboard" : false
        } ).addTo( this._map );

        const title = label || ( kind === "start" ? _lcs( "Start" ) : _lcs( "Ziel" ) );
        marker.bindTooltip( title, { "direction" : "top", "offset" : [ 0, -8 ] } );
    }

    /**
     * The tour's own waypoints — the hut halfway up, the viewpoint at the top.
     *
     * A waypoint may carry `placeId`, which is the whole reason they are a
     * relation rather than copied text: the hut on this map is the same record
     * the Gastronomie screen holds, with the same opening hours.
     *
     * The eT4 feed does not currently supply waypoints, so this renders
     * nothing for imported tours; it is here for the ones an operator or a
     * richer feed will add.
     */
    _renderWaypoints( tour )
    {
        const waypoints = Array.isArray( tour.waypoints ) ? tour.waypoints : [];

        waypoints.forEach( ( waypoint, index ) =>
        {
            const lat = Number( waypoint && waypoint.latitude );
            const lon = Number( waypoint && waypoint.longitude );

            if ( !Number.isFinite( lat ) || !Number.isFinite( lon ) )
            {
                return;
            }

            const marker = L.marker( [ lat, lon ], {
                "icon" : L.divIcon( {
                    "className" : "",
                    "html" : '<span class="tr-map__pin tr-map__pin--waypoint">' +
                        ( waypoint.order != null ? waypoint.order : index + 1 ) + '</span>',
                    "iconSize" : [ 20, 20 ],
                    "iconAnchor" : [ 10, 10 ]
                } )
            } ).addTo( this._map );

            const parts = [ this._escape( waypoint.name || _lcs( "Wegpunkt" ) ) ];
            if ( waypoint.note ) parts.push( this._escape( waypoint.note ) );
            if ( waypoint.placeId ) parts.push( '<em>' + this._escape( _lcs( "Verknüpfter Eintrag" ) ) + '</em>' );

            marker.bindPopup( '<strong>' + parts[ 0 ] + '</strong>' +
                parts.slice( 1 ).map( ( p ) => '<br>' + p ).join( "" ) );
        } );
    }

    /**
     * What the route passes: huts, viewpoints and restaurants within a few
     * hundred metres of the track (GET /tour/platform/:id/nearby).
     *
     * Best-effort on purpose — the map is already drawn and usable, and an
     * unreachable lookup should cost the pins, not the page.
     */
    async _loadNearbyPlaces( tour )
    {
        this._nearby = [];

        try
        {
            const { result } = await this.app.api.get(
                "/tour/platform/" + tour.id + "/nearby?radius=" + TourDetail.NEARBY_RADIUS_M
            );

            if ( !result || +result.status !== 200 || !result.data )
            {
                return;
            }

            // The dialog may have been closed, or another tour opened, while
            // this was in flight.
            if ( !this._map || !this._tour || this._tour.id !== tour.id )
            {
                return;
            }

            this._nearby = Array.isArray( result.data.items ) ? result.data.items : [];
            this._nearby.forEach( ( place ) => this._addPlaceMarker( place ) );
            this._setMapCaption( result.data );
        }
        catch ( e )
        {
            console.error( e );
        }
    }

    _addPlaceMarker( place )
    {
        const lat = Number( place.latitude );
        const lon = Number( place.longitude );

        if ( !Number.isFinite( lat ) || !Number.isFinite( lon ) || !this._map )
        {
            return;
        }

        const kind = place.type === "gastro" ? "gastro" : "poi";

        const marker = L.marker( [ lat, lon ], {
            "icon" : L.divIcon( {
                "className" : "",
                "html" : '<span class="tr-map__pin tr-map__pin--' + kind + '">' +
                    '<i class="icon icon--' + ( kind === "gastro" ? "utensils" : "landmark" ) + '"></i></span>',
                "iconSize" : [ 22, 22 ],
                "iconAnchor" : [ 11, 11 ]
            } )
        } ).addTo( this._map );

        const categories = ( place.categories || [] ).slice( 0, 3 ).join( ", " );

        marker.bindPopup(
            '<strong>' + this._escape( place.name ) + '</strong>' +
            ( categories ? '<br>' + this._escape( categories ) : "" ) +
            ( place.city ? '<br>' + this._escape( place.city ) : "" ) +
            '<br><em>' + this._escape( _lcs( "{distance} von der Strecke", {
                distance : this._distanceText( place.distanceMeters )
            } ) ) + '</em>'
        );
    }

    /**
     * The caption doubles as the legend: without it a reader has to guess what
     * the coloured pins mean.
     */
    _setMapCaption( nearbyPayload = null )
    {
        const caption = this.querySelector( '[data-id="tr-map-caption"]' );
        const parts = [ _lcs( "Mausrad-Zoom ist aus, damit der Dialog scrollbar bleibt — nutze die +/− Schaltflächen." ) ];

        if ( nearbyPayload )
        {
            const count = nearbyPayload.count || 0;
            const radius = this._distanceText( nearbyPayload.radiusMeters );

            parts.unshift( count === 0
                ? _lcs( "Keine Gastronomie oder Sehenswürdigkeiten innerhalb von {radius} entlang der Strecke.", { radius } )
                : _lcs( "{count} Einträge innerhalb von {radius} entlang der Strecke", { count, radius } ) +
                  ( nearbyPayload.isTruncated ? " " + _lcs( "(gekürzt — die nächstgelegenen zuerst)" ) : "" ) + "." );
        }

        caption.textContent = parts.join( " " );
    }

    _distanceText( metres )
    {
        const value = Math.round( +metres );
        if ( !Number.isFinite( value ) )
        {
            return "—";
        }
        return value >= 1000 ? ( Math.round( value / 100 ) / 10 ) + " km" : value + " m";
    }

    _destroyMap()
    {
        if ( this._map )
        {
            try { this._map.remove(); } catch ( e ) { /* container is going away anyway */ }
            this._map = null;
            this._mapLayer = null;
        }
        this._nearby = [];
    }

    _renderImages( tour )
    {
        const el = this.querySelector( '[data-id="tr-images"]' );
        const images = Array.isArray( tour.images ) ? tour.images : [];

        if ( images.length === 0 )
        {
            el.hidden = true;
            el.innerHTML = "";
            return;
        }

        el.innerHTML = images.slice( 0, 8 ).map( ( image ) =>
            '<figure class="plc-detail__image">' +
                '<img src="' + this._escape( image.url ) + '" alt="' + this._escape( image.alt || "" ) + '" loading="lazy" />' +
                // The credit is a licence condition, not decoration.
                ( image.credit
                    ? '<figcaption>' + this._escape( image.credit ) +
                      ( image.license ? ' · ' + this._escape( image.license ) : "" ) + '</figcaption>'
                    : "" ) +
            '</figure>'
        ).join( "" );
        el.hidden = false;
    }

    _renderTexts( tour )
    {
        const teaser = this.querySelector( '[data-id="tr-teaser"]' );
        teaser.textContent = tour.teaser || "";
        teaser.hidden = !tour.teaser;

        const description = this.querySelector( '[data-id="tr-description"]' );
        description.innerHTML = "";
        if ( tour.description )
        {
            String( tour.description ).split( /\n{2,}/ ).forEach( ( paragraph ) =>
            {
                const p = document.createElement( "p" );
                p.textContent = paragraph.trim();
                if ( p.textContent.length > 0 ) description.appendChild( p );
            } );
        }
        description.hidden = !tour.description;
    }

    _renderRoute( tour )
    {
        const start = tour.startPoint || {};
        const end = tour.endPoint || {};

        this._facts( "tr-route", [
            [ _lcs( "Start" ), start.name ],
            [ _lcs( "Startadresse" ), start.address ],
            [ _lcs( "Koordinaten" ), tour.startLatitude != null && tour.startLongitude != null
                ? ( +tour.startLatitude ).toFixed( 5 ) + ", " + ( +tour.startLongitude ).toFixed( 5 ) : null ],
            [ _lcs( "Ziel" ), tour.routeType === "loop" ? _lcs( "Rundweg — zurück zum Start" ) : end.name ],
            [ _lcs( "Anfahrt" ), start.directions ],
            [ _lcs( "Wegpunkte" ), Array.isArray( tour.waypoints ) && tour.waypoints.length > 0
                ? _lcs( "{count} markiert", { count: tour.waypoints.length } ) : null ]
        ] );
    }

    _renderConditions( tour )
    {
        const surfaces = ( tour.surfaces || [] )
            .map( ( s ) => SURFACE_TITLES[ s ] ? _lcs( SURFACE_TITLES[ s ] ) : s ).join( ", " );
        const seasons = ( tour.seasons || [] )
            .map( ( s ) => SEASON_TITLES[ s ] ? _lcs( SEASON_TITLES[ s ] ) : s ).join( ", " );
        const suitable = [
            tour.isDogFriendly ? _lcs( "Hunde erlaubt" ) : null,
            tour.isStrollerFriendly ? _lcs( "Kinderwagentauglich" ) : null
        ].filter( Boolean ).join( " · " );

        this._facts( "tr-conditions", [
            [ _lcs( "Untergrund" ), surfaces ],
            [ _lcs( "Jahreszeiten" ), seasons ],
            [ _lcs( "Geeignet für" ), suitable ],
            [ _lcs( "Hinweise" ), tour.conditionNote ],
            [ "GPX", tour.gpxUrl ? this._link( tour.gpxUrl, _lcs( "Track herunterladen" ) ) : null, true ],
            [ _lcs( "Website" ), tour.websiteUrl ? this._link( tour.websiteUrl, tour.websiteUrl ) : null, true ]
        ] );
    }

    _renderProvenance( tour, sourceName )
    {
        this._facts( "tr-provenance", [
            [ _lcs( "Quelle" ), sourceName ],
            [ _lcs( "Anbieter-ID" ), tour.sourceId ],
            [ _lcs( "Zuletzt synchronisiert" ), this._dateText( tour.sourceSyncedAt ) ],
            [ _lcs( "Zuletzt geändert" ), this._dateText( tour.updatedAt ) ],
            [ _lcs( "Anbieter-Seite" ), tour.sourceUrl
                ? this._link( tour.sourceUrl, _lcs( "Original ansehen" ) ) : null, true ]
        ] );
    }

    _renderPublishAction( tour )
    {
        const isPublished = tour.status === "published";

        this._publishLabel.textContent = isPublished ? _lcs( "Zurückziehen" ) : _lcs( "Veröffentlichen" );
        this._publish.classList.toggle( "btn--primary", !isPublished );
        this._publish.classList.toggle( "btn--ghost", isPublished );

        this._hint.textContent = tour.isLocked
            ? _lcs( "Diese Tour ist gegen den Import gesperrt." )
            : _lcs( "Eine Änderung sperrt die Tour gegen den nächsten Import." );
    }

    /* -------------------------------- Actions -------------------------------- */

    async togglePublished()
    {
        if ( !this._tour )
        {
            return;
        }

        const next = this._tour.status === "published" ? "draft" : "published";

        this._publish.disabled = true;
        try
        {
            const { result } = await this.app.api.patch( "/tour/platform/" + this._tour.id, { "status" : next } );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected update response." );
            }

            this._tour = result.data || this._tour;
            this._renderBadges( this._tour );
            this._renderPublishAction( this._tour );

            this.app.view.toast(
                next === "published"
                    ? _lcs( "„{name}“ ist jetzt für Gäste sichtbar.", { name: this._tour.name } )
                    : _lcs( "„{name}“ wurde zurückgezogen.", { name: this._tour.name } ),
                "success"
            );

            this.dispatchEvent( new CustomEvent( "tour-saved", {
                bubbles : true, detail : { tour : this._tour }
            } ) );
        }
        catch ( err )
        {
            this._showError( this._errorForStatus( +err?.status ) );
        }
        finally
        {
            this._publish.disabled = false;
        }
    }

    /* -------------------------------- Helpers -------------------------------- */

    _facts( id, rows )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );

        const html = ( rows || [] )
            .filter( ( [ , value ] ) => value !== null && value !== undefined && value !== "" )
            .map( ( [ label, value, isHtml ] ) =>
                '<div class="plc-detail__fact">' +
                    '<dt>' + this._escape( label ) + '</dt>' +
                    '<dd>' + ( isHtml ? value : this._escape( value ) ) + '</dd>' +
                '</div>'
            ).join( "" );

        el.innerHTML = html || '<p class="plc-detail__empty">' + this._escape( _lcs( "Keine Angaben" ) ) + '</p>';
    }

    _link( href, label )
    {
        return '<a href="' + this._escape( href ) + '" target="_blank" rel="noopener noreferrer">' +
            this._escape( label ) + '</a>';
    }

    _durationText( minutes )
    {
        const total = Math.round( +minutes );
        if ( !Number.isFinite( total ) || total <= 0 )
        {
            return null;
        }
        const hours = Math.floor( total / 60 );
        const rest = total % 60;
        return hours > 0 ? `${hours}:${String( rest ).padStart( 2, "0" )} h` : `${rest} min`;
    }

    _statusLabel( status )
    {
        const label = ( {
            published : "Veröffentlicht", draft : "Entwurf", archived : "Archiviert"
        } )[ status ];
        return label ? _lcs( label ) : ( status || "—" );
    }

    _dateText( value )
    {
        if ( !value ) return null;
        const date = new Date( value );
        if ( isNaN( date.getTime() ) ) return null;
        return _lcs( "{time} Uhr", {
            time : date.toLocaleString( locale(), {
                day : "2-digit", month : "2-digit", year : "numeric", hour : "2-digit", minute : "2-digit"
            } )
        } );
    }

    _set( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    _errorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Die Änderung wurde abgelehnt." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Die Tour wurde nicht gefunden." );
            default:  return _lcs( "Speichern fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    _showError( message )
    {
        this._error.textContent = message;
        this._error.hidden = false;
    }

    _clearError()
    {
        this._error.textContent = "";
        this._error.hidden = true;
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }
}

customElements.define( "tour-detail", TourDetail );
